import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
type RegisterFormProps = {
  userType: 'customer' | 'vendor';
  onSubmit: (data: RegisterFormData) => void;
};
export type RegisterFormData = {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone?: string;
  companyName?: string;
  acceptTerms: boolean;
};
export function RegisterForm({
  userType,
  onSubmit
}: RegisterFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: {
      errors,
      isSubmitting
    }
  } = useForm<RegisterFormData>();
  const password = watch('password');
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  return <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-id="element-81">
      <div data-id="element-82">
        <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-83">
          Full Name
        </label>
        <div className="mt-1 relative" data-id="element-84">
          <input id="name" type="text" autoComplete="name" className={`block w-full px-4 py-3 rounded-md border ${errors.name ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-primary focus:border-primary text-gray-900`} {...register('name', {
          required: 'Full name is required'
        })} placeholder="Enter your full name" data-id="element-85" />
          {errors.name && <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-86">
              <AlertCircle className="h-5 w-5 text-red-500" data-id="element-87" />
            </div>}
        </div>
        {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-88">{errors.name.message}</p>}
      </div>
      <div data-id="element-89">
        <label htmlFor="email" className="block text-sm font-medium text-gray-700" data-id="element-90">
          Email address
        </label>
        <div className="mt-1 relative" data-id="element-91">
          <input id="email" type="email" autoComplete="email" className={`block w-full px-4 py-3 rounded-md border ${errors.email ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-primary focus:border-primary text-gray-900`} {...register('email', {
          required: 'Email is required',
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: 'Invalid email address'
          }
        })} placeholder="Enter your email" data-id="element-92" />
          {errors.email && <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-93">
              <AlertCircle className="h-5 w-5 text-red-500" data-id="element-94" />
            </div>}
        </div>
        {errors.email && <p className="mt-1 text-sm text-red-600" data-id="element-95">{errors.email.message}</p>}
      </div>
      {userType === 'customer' && <div data-id="element-96">
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700" data-id="element-97">
            Phone Number
          </label>
          <div className="mt-1 relative" data-id="element-98">
            <input id="phone" type="tel" autoComplete="tel" className={`block w-full px-4 py-3 rounded-md border ${errors.phone ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-primary focus:border-primary text-gray-900`} {...register('phone', {
          required: 'Phone number is required',
          pattern: {
            value: /^[\d\+\-\s]+$/,
            message: 'Invalid phone number'
          }
        })} placeholder="Enter your phone number" data-id="element-99" />
            {errors.phone && <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-100">
                <AlertCircle className="h-5 w-5 text-red-500" data-id="element-101" />
              </div>}
          </div>
          {errors.phone && <p className="mt-1 text-sm text-red-600" data-id="element-102">{errors.phone.message}</p>}
        </div>}
      {userType === 'vendor' && <div data-id="element-103">
          <label htmlFor="companyName" className="block text-sm font-medium text-gray-700" data-id="element-104">
            Company Name
          </label>
          <div className="mt-1 relative" data-id="element-105">
            <input id="companyName" type="text" className={`block w-full px-4 py-3 rounded-md border ${errors.companyName ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-primary focus:border-primary text-gray-900`} {...register('companyName', {
          required: 'Company name is required'
        })} placeholder="Enter your company name" data-id="element-106" />
            {errors.companyName && <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-107">
                <AlertCircle className="h-5 w-5 text-red-500" data-id="element-108" />
              </div>}
          </div>
          {errors.companyName && <p className="mt-1 text-sm text-red-600" data-id="element-109">
              {errors.companyName.message}
            </p>}
        </div>}
      <div data-id="element-110">
        <label htmlFor="password" className="block text-sm font-medium text-gray-700" data-id="element-111">
          Password
        </label>
        <div className="mt-1 relative" data-id="element-112">
          <input id="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" className={`block w-full px-4 py-3 rounded-md border ${errors.password ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-primary focus:border-primary pr-10 text-gray-900`} {...register('password', {
          required: 'Password is required',
          minLength: {
            value: 8,
            message: 'Password must be at least 8 characters'
          },
          pattern: {
            value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
            message: 'Password must include uppercase, lowercase, number and special character'
          }
        })} placeholder="Create a password" data-id="element-113" />
          <button type="button" className="absolute inset-y-0 right-0 flex items-center pr-3" onClick={togglePasswordVisibility} data-id="element-114">
            {showPassword ? <EyeOff className="h-5 w-5 text-gray-400" data-id="element-115" /> : <Eye className="h-5 w-5 text-gray-400" data-id="element-116" />}
          </button>
        </div>
        {errors.password && <p className="mt-1 text-sm text-red-600" data-id="element-117">{errors.password.message}</p>}
      </div>
      <div data-id="element-118">
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700" data-id="element-119">
          Confirm Password
        </label>
        <div className="mt-1 relative" data-id="element-120">
          <input id="confirmPassword" type={showPassword ? 'text' : 'password'} className={`block w-full px-4 py-3 rounded-md border ${errors.confirmPassword ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-primary focus:border-primary pr-10 text-gray-900`} {...register('confirmPassword', {
          required: 'Please confirm your password',
          validate: value => value === password || 'Passwords do not match'
        })} placeholder="Confirm your password" data-id="element-121" />
          <button type="button" className="absolute inset-y-0 right-0 flex items-center pr-3" onClick={togglePasswordVisibility} data-id="element-122">
            {showPassword ? <EyeOff className="h-5 w-5 text-gray-400" data-id="element-123" /> : <Eye className="h-5 w-5 text-gray-400" data-id="element-124" />}
          </button>
        </div>
        {errors.confirmPassword && <p className="mt-1 text-sm text-red-600" data-id="element-125">
            {errors.confirmPassword.message}
          </p>}
      </div>
      <div className="flex items-start" data-id="element-126">
        <div className="flex items-center h-5" data-id="element-127">
          <input id="terms" type="checkbox" className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded" {...register('acceptTerms', {
          required: 'You must accept the terms and conditions'
        })} data-id="element-128" />
        </div>
        <div className="ml-3 text-sm" data-id="element-129">
          <label htmlFor="terms" className="font-medium text-gray-700" data-id="element-130">
            I agree to the{' '}
            <a href="#" className="text-primary hover:text-primary/80" data-id="element-131">
              terms and conditions
            </a>
          </label>
          {errors.acceptTerms && <p className="mt-1 text-sm text-red-600" data-id="element-132">
              {errors.acceptTerms.message}
            </p>}
        </div>
      </div>
      <div data-id="element-133">
        <button type="submit" disabled={isSubmitting} className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-70" data-id="element-134">
          {isSubmitting ? 'Creating account...' : `Register as ${userType === 'customer' ? 'Customer' : 'Vendor'}`}
        </button>
      </div>
    </form>;
}